import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'CustomPageStyle/byToken';

interface CustomPageStyleResponse {
	html?: string;
	hasCustomization: boolean;
	error?: string;
}

/**
 * Fetches account-level custom page styles.
 *
 * This hook fetches the DEFAULT account-level branding customization.
 * This is the fallback when surveys/meetings don't have individual overrides.
 *
 * Relationship with Per-Survey/Meeting Branding:
 * - This hook returns account-level defaults
 * - Surveys use useCustomPageStyleByToken (which falls back to this)
 * - Meetings use useCustomPageStyleByShortCode (which falls back to this)
 * - Backend implements the fallback chain automatically
 *
 * Fallback Chain (for reference):
 * 1. Individual survey/meeting override (handled by backend)
 * 2. Account-level customization (THIS hook's data)
 * 3. System default (no customization)
 *
 * Query Key Structure:
 * - [BASE_QUERY_KEY, type, impersonationContext]
 * - Account-level styles are cached separately from individual overrides
 * - Impersonation context ensures multi-account support
 *
 * @param type - Type of customization (Header, Footer, etc.)
 * @param enabled - Whether the query should execute
 * @param impersonationContext - Optional impersonation context for admin features
 */
export const useCustomPageStyleByType = ({
	type,
	enabled = true,
	impersonationContext,
}: {
	type: Api.CustomPageType;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) => {
	const userSession = useUserSession();

	return useQuery<CustomPageStyleResponse>({
		enabled,
		queryFn: async (): Promise<CustomPageStyleResponse> => {
			try {
				const response = await userSession.webServiceHelper.callAsync<Api.CustomPageStyle>(
					Api.ImpersonationBroker.composeApiUrl({
						impersonationContext,
						urlPath: `CustomPageStyle/${type}`,
					}),
					'GET'
				);

				if (!response || !response.css || response.css.trim() === '') {
					return { hasCustomization: false, error: 'No custom styles found' };
				}

				return {
					hasCustomization: true,
					html: response.css,
				};
			} catch (networkError) {
				const apiError = Api.asApiError(networkError);
				if (apiError.systemCode === 404) {
					return { hasCustomization: false, error: 'No custom styles found' };
				}
				console.error('Network error fetching custom page style:', networkError);
				return { hasCustomization: false, error: 'Network error occurred' };
			}
		},
		queryKey: [BASE_QUERY_KEY, type, impersonationContext],
		retry: (failureCount, error) => {
			// Only retry on network errors, not on 4xx client errors
			return failureCount < 2 && !(error as any)?.message?.includes('HTTP 4');
		},
	});
};

/**
 * Invalidates all account-level custom page style queries.
 *
 * Use this when account-level branding is updated to ensure caches are refreshed.
 * Note: This does NOT invalidate individual survey/meeting override caches.
 * Those are managed separately by their respective hooks.
 */
export const invalidateCustomPageStyleByToken = () => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
};
